import { Sprite } from 'pixi.js';
import type { Atlas } from './atlas';

/**
 * 캐릭터 스프라이트 애니메이터.
 *
 * 그림이 측면만 있고 좌우 구분이 없으므로, 왼쪽으로 갈 때는 좌우 반전해서 쓴다.
 * 위/아래로 갈 때는 정면·뒷모습 그림을 쓰되 걷기 프레임이 없어서 살짝 위아래로 흔든다.
 */

export type AnimState = 'idle' | 'walk' | 'attack' | 'cast' | 'dead' | 'transform';
export type Facing = 'down' | 'up' | 'side';

/** characters.json 의 anim 블록. 값은 아틀라스 프레임 번호 배열. */
export interface AnimDef {
  idleDown: number[];
  idleUp: number[];
  idleSide: number[];
  walkDown: number[];
  walkUp: number[];
  walkSide: number[];
  attack: number[];
  cast: number[];
  dead: number[];
  /** 프레임마다 발을 세울 가로 위치(0~1). 없으면 가운데 */
  anchorX?: Record<string, number>;
  /** 왼쪽을 보고 그려진 프레임들 — 나머지(오른쪽을 보는 그림)와 방향을 맞추려고 좌우를 한 번 더 뒤집는다 */
  mirror?: number[];
}

/** 한 번 재생하고 끝나는 동작들 (끝나면 idle/walk 로 돌아간다) */
const ONE_SHOT: Partial<Record<AnimState, number>> = {
  attack: 0.28,
  cast: 0.5,
};

const FPS = 9;

/** 발이 히트박스보다 조금 아래에 오도록 내리는 양(px) */
export const FOOT_DROP = 10;

export class CharacterAnimator {
  private state: AnimState = 'idle';
  private facing: Facing = 'down';
  private flip = false;
  private clock = 0;
  private oneShotLeft = 0;
  /** 정면/뒷모습 걷기용 위아래 흔들림 */
  bobOffset = 0;
  /** 변신: 재생할 프레임과 한 장당 시간. 끝나면 다음 모습(nextAtlas/nextDef)으로 바뀐다 */
  private transformFrames: number[] = [];
  private transformStep = 0.2;
  private nextAtlas: Atlas | null = null;
  private nextDef: AnimDef | null = null;
  /** 재생할 프레임을 가져올 아틀라스 (풀릴 때는 변신 모습의 그림을 재생하고 원래 모습으로 끝난다) */
  private srcAtlas: Atlas | null = null;
  private srcDef: AnimDef | null = null;
  /** 지금 보여 주는 그림이 왼쪽을 보고 그려졌는가 */
  private mirrored = false;

  constructor(
    readonly sprite: Sprite,
    private atlas: Atlas,
    private def: AnimDef,
  ) {
    // 프레임마다 높이가 다르므로 바닥 기준으로 붙인다. 그래야 포즈가 바뀌어도 발이 안 뜬다.
    sprite.anchor.set(0.5, 1);
  }

  /** 짧게 한 번 재생. 다른 동작이 재생 중이면 무시하고, 같은 동작이면 그만큼 늘인다(연달아 휘두를 때 자세가 풀리지 않게). */
  trigger(state: 'attack' | 'cast'): void {
    if (this.state === 'dead' || this.state === 'transform') return;
    const dur = ONE_SHOT[state] ?? 0.3;
    if (this.oneShotLeft > 0) {
      if (this.state === state) this.oneShotLeft = dur;
      return;
    }
    this.state = state;
    this.oneShotLeft = dur;
    this.clock = 0;
  }

  /** 바로 다른 모습으로 (변신 연출 없이) */
  setForm(atlas: Atlas, def: AnimDef): void {
    this.atlas = atlas;
    this.def = def;
    this.transformFrames = [];
    if (this.state === 'transform') this.state = 'idle';
  }

  /**
   * 변신: frames 를 차례로 재생하고 끝나면 새 모습(atlas/def)이 된다. 프레임은 새 모습의 아틀라스 번호 —
   * from 을 주면 그 아틀라스의 번호(변신이 풀릴 때: 변신 모습의 그림을 재생하고 원래 모습으로 끝난다).
   * 재생하는 동안에도 움직일 수 있다(그림만 바뀐다).
   */
  playTransform(atlas: Atlas, def: AnimDef, frames: number[], step = 0.2, from?: { atlas: Atlas; def: AnimDef }): void {
    if (frames.length === 0) {
      this.setForm(atlas, def);
      return;
    }
    this.nextAtlas = atlas;
    this.nextDef = def;
    this.srcAtlas = from?.atlas ?? atlas;
    this.srcDef = from?.def ?? def;
    this.transformFrames = frames;
    this.transformStep = step;
    this.state = 'transform';
    this.oneShotLeft = frames.length * step;
    this.clock = 0;
  }

  /** 변신 중인가 */
  get transforming(): boolean {
    return this.state === 'transform';
  }

  kill(): void {
    this.state = 'dead';
    this.oneShotLeft = 0;
    this.clock = 0;
  }

  revive(): void {
    this.state = 'idle';
    this.oneShotLeft = 0;
  }

  /**
   * @param dt      경과 시간(초)
   * @param moveX   이동 방향 x (-1..1)
   * @param moveY   이동 방향 y (-1..1)
   */
  update(dt: number, moveX: number, moveY: number): void {
    this.clock += dt;

    if (this.state === 'transform') {
      const i = Math.min(this.transformFrames.length - 1, Math.floor(this.clock / this.transformStep));
      const f = this.transformFrames[i];
      const tex = this.srcAtlas?.frames[f];
      if (tex) this.sprite.texture = tex;
      this.sprite.anchor.x = this.srcDef?.anchorX?.[f] ?? 0.5;
      this.mirrored = this.srcDef?.mirror?.includes(f) ?? false;
      this.oneShotLeft -= dt;
      if (this.oneShotLeft <= 0 && this.nextAtlas && this.nextDef) {
        this.atlas = this.nextAtlas;
        this.def = this.nextDef;
        this.nextAtlas = null;
        this.nextDef = null;
        this.transformFrames = [];
        this.state = 'idle';
      }
      this.bobOffset = 0;
      this.sprite.scale.x = Math.abs(this.sprite.scale.x) * (this.flip !== this.mirrored ? -1 : 1);
      return;
    }

    if (this.state !== 'dead') {
      if (this.oneShotLeft > 0) {
        this.oneShotLeft -= dt;
        if (this.oneShotLeft <= 0) this.state = 'idle';
      }

      const moving = Math.abs(moveX) > 0.01 || Math.abs(moveY) > 0.01;
      if (moving) {
        if (Math.abs(moveX) >= Math.abs(moveY) * 0.9) {
          this.facing = 'side';
          this.flip = moveX < 0;
        } else {
          this.facing = moveY > 0 ? 'down' : 'up';
        }
      }
      if (this.oneShotLeft <= 0) this.state = moving ? 'walk' : 'idle';
    }

    const frames = this.pickFrames();
    if (frames.length > 0) {
      // 휘두르기·시전이 여러 장이면 한 번 재생하는 동안 다 지나가게 빨리 넘긴다
      const oneShot = ONE_SHOT[this.state];
      const fps = oneShot && frames.length > 1 ? Math.max(FPS, frames.length / oneShot) : FPS;
      const f = frames[Math.floor(this.clock * fps) % frames.length];
      const tex = this.atlas.frames[f];
      if (tex) this.sprite.texture = tex;
      this.sprite.anchor.x = this.def.anchorX?.[f] ?? 0.5;
      this.mirrored = this.def.mirror?.includes(f) ?? false;
    }

    // 정면/뒷모습은 걷기 그림이 없으니 흔들어서 움직이는 느낌만 준다
    const bobbing = this.state === 'walk' && this.facing !== 'side';
    this.bobOffset = bobbing ? (Math.floor(this.clock * FPS) % 2 === 0 ? 0 : -1) : 0;

    // 그림은 오른쪽을 본다고 보고, 왼쪽을 보고 그려진 프레임(mirror)이면 한 번 더 뒤집는다
    this.sprite.scale.x = Math.abs(this.sprite.scale.x) * (this.flip !== this.mirrored ? -1 : 1);
  }

  private pickFrames(): number[] {
    const d = this.def;
    switch (this.state) {
      case 'dead':
        return d.dead;
      case 'attack':
        return d.attack;
      case 'cast':
        return d.cast;
      case 'walk':
        return this.facing === 'side' ? d.walkSide : this.facing === 'up' ? d.walkUp : d.walkDown;
      default:
        return this.facing === 'side' ? d.idleSide : this.facing === 'up' ? d.idleUp : d.idleDown;
    }
  }
}
